/**
 * The keyboard-shortcut registry (spec M0-F4 §7.9, **re-measured in B10**).
 *
 * Round 1 wrote: *"every binding below is ASSUMED: monday's shortcut sheet was never opened
 * (open question 105)"*. It has now been opened — `Shift + ?` on a board — and photographed:
 * a **400 × 803** right-hand panel at (1024, 81), radius 4, titled *Keyboard Shortcuts*
 * (18 px / 700), a `Shift + Question Mark to open this menu` hint, then two sections
 * (16 px / 700) — **System** (5) and **Board** (20) — of 14 px rows at a **48 px** pitch with
 * the keys drawn as chips on the right `[c:shortcuts]`. **q105 is closed.**
 *
 * The 25 entries below are monday's, verbatim, in monday's order, with monday's labels. Four
 * bindings of our own (notifications, update feed, My Work, back) have no monday equivalent;
 * they stay registered so the app keeps working and are marked `hidden`, so the **sheet** is
 * monday's list exactly.
 *
 * > A monday defect worth recording: the `Search Board` row renders its key as the raw i18n
 * > token **`keyboard_shortcuts.buttons.f`** instead of `F`. Ours prints `F`.
 */
import { useEffect } from 'react';

export type ShortcutScope = 'global' | 'board' | 'grid';

export interface ShortcutDefinition {
  id: string;
  /** Human-readable, in the order the sheet lists them. `Mod` renders as ⌘ / Ctrl. */
  keys: string[];
  description: string;
  scope: ShortcutScope;
  /** `Esc` is the only shortcut that fires while focus is inside an input. */
  allowInInput?: boolean;
  /** The section the sheet groups it under: monday has exactly `System` and `Board`. */
  group: string;
  /** Ours, with no monday equivalent: bound, but absent from the sheet (§3.2). */
  hidden?: boolean;
}

export const SHORTCUTS: readonly ShortcutDefinition[] = [
  /* ── System (`[c:shortcuts]`, 5 rows) ─────────────────────────────────── */
  { id: 'search.open', keys: ['Mod+K'], description: 'Quick Search', scope: 'global', group: 'System' },
  {
    id: 'item.close',
    keys: ['Esc'],
    description: 'Close Item Page',
    scope: 'global',
    allowInInput: true,
    group: 'System',
  },
  {
    id: 'escape',
    keys: ['Esc'],
    description: 'Close Dialogs',
    scope: 'global',
    allowInInput: true,
    group: 'System',
  },
  {
    id: 'search.close',
    keys: ['Esc'],
    description: 'Close Search',
    scope: 'global',
    allowInInput: true,
    group: 'System',
  },
  {
    id: 'sidebar.toggle',
    // monday's chip reads `Control` + `Dot`.
    keys: ['Mod+.'],
    description: 'Open/Close Navigation',
    scope: 'global',
    group: 'System',
  },

  /* ── Board (`[c:shortcuts]`, 20 rows) ─────────────────────────────────── */
  { id: 'grid.down', keys: ['ArrowDown'], description: 'Navigate Down', scope: 'grid', group: 'Board' },
  { id: 'grid.up', keys: ['ArrowUp'], description: 'Navigate Up', scope: 'grid', group: 'Board' },
  { id: 'grid.left', keys: ['ArrowLeft'], description: 'Navigate Left', scope: 'grid', group: 'Board' },
  { id: 'grid.right', keys: ['ArrowRight'], description: 'Navigate Right', scope: 'grid', group: 'Board' },
  { id: 'grid.nextCell', keys: ['Tab'], description: 'Navigate To Next Cell', scope: 'grid', group: 'Board' },
  {
    id: 'grid.previousCell',
    keys: ['Shift+Tab'],
    description: 'Navigate To Previous Cell',
    scope: 'grid',
    group: 'Board',
  },
  { id: 'grid.edit', keys: ['Enter'], description: 'Edit Cell Content', scope: 'grid', group: 'Board' },
  {
    id: 'grid.clear',
    keys: ['Backspace'],
    description: 'Clear Cell Content',
    scope: 'grid',
    group: 'Board',
  },
  { id: 'item.open', keys: ['Space'], description: 'Open Column View', scope: 'grid', group: 'Board' },
  { id: 'board.search', keys: ['Mod+F'], description: 'Search Board', scope: 'board', group: 'Board' },
  { id: 'grid.copy', keys: ['Mod+C'], description: 'Copy Cell Content', scope: 'grid', group: 'Board' },
  { id: 'grid.paste', keys: ['Mod+V'], description: 'Paste Cell Content', scope: 'grid', group: 'Board' },
  { id: 'grid.cut', keys: ['Mod+X'], description: 'Cut Cell Content', scope: 'grid', group: 'Board' },
  { id: 'grid.selectItem', keys: ['Mod+Enter'], description: 'Select Item', scope: 'grid', group: 'Board' },
  {
    id: 'grid.selectRange',
    keys: ['Shift+Enter'],
    description: 'Select Range Of Items',
    scope: 'grid',
    group: 'Board',
  },
  {
    id: 'board.newGroup',
    keys: ['Mod+Shift+G'],
    description: 'Create New Group',
    scope: 'board',
    group: 'Board',
  },
  {
    id: 'board.newColumn',
    keys: ['Mod+Shift+C'],
    description: 'Create New Column',
    scope: 'board',
    group: 'Board',
  },
  {
    id: 'board.activityLog',
    keys: ['Mod+Shift+L'],
    description: 'Open Activity Log',
    scope: 'board',
    group: 'Board',
  },
  {
    id: 'board.sortAnother',
    // A modified **click**, not a keystroke; the sheet lists it and so does ours.
    keys: ['Mod+Click'],
    description: 'Sort another column',
    scope: 'board',
    group: 'Board',
  },
  {
    id: 'board.collapseGroups',
    keys: ['Mod+G'],
    description: 'Collapse/Expand All Groups',
    scope: 'board',
    group: 'Board',
  },

  /* ── Ours, absent from monday's sheet (§3.2): bound, never listed ─────── */
  {
    id: 'shortcuts.open',
    keys: ['?'],
    description: 'Keyboard shortcuts',
    scope: 'global',
    group: 'System',
    hidden: true,
  },
  {
    id: 'notifications.toggle',
    keys: ['N'],
    description: 'Notifications',
    scope: 'global',
    group: 'System',
    hidden: true,
  },
  { id: 'inbox.open', keys: ['I'], description: 'Update feed', scope: 'global', group: 'System', hidden: true },
  { id: 'mywork.open', keys: ['W'], description: 'My work', scope: 'global', group: 'System', hidden: true },
  { id: 'nav.back', keys: ['Mod+['], description: 'Back', scope: 'global', group: 'System', hidden: true },
  {
    id: 'board.newItem',
    keys: ['Mod+Shift+N'],
    description: 'New item',
    scope: 'board',
    group: 'Board',
    hidden: true,
  },
] as const;

export type ShortcutId = (typeof SHORTCUTS)[number]['id'];

const BY_ID = new Map(SHORTCUTS.map((shortcut) => [shortcut.id, shortcut]));

export function shortcutById(id: string): ShortcutDefinition | undefined {
  return BY_ID.get(id);
}

/** Groups, in registry order, for the sheet (K2). */
export function shortcutGroups(): { group: string; shortcuts: ShortcutDefinition[] }[] {
  const groups: { group: string; shortcuts: ShortcutDefinition[] }[] = [];
  for (const shortcut of SHORTCUTS) {
    if (shortcut.hidden) continue;
    const existing = groups.find((entry) => entry.group === shortcut.group);
    if (existing) existing.shortcuts.push(shortcut);
    else groups.push({ group: shortcut.group, shortcuts: [shortcut] });
  }
  return groups;
}

/**
 * Key names the sheet spells one way and `KeyboardEvent.key` another. Both sides go through
 * this table, so the registry can read `Esc` and `Space` while the event says `Escape` and
 * `' '` — and neither has to know about the other.
 */
const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  ' ': 'space',
  spacebar: 'space',
  del: 'delete',
  return: 'enter',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
};

const canonicalKey = (key: string): string => {
  const lower = key.toLowerCase();
  return KEY_ALIASES[lower] ?? lower;
};

/** `Mod+Shift+N` → the normalised form a `KeyboardEvent` produces. */
export function normaliseBinding(binding: string): string {
  const parts = binding.split('+').map((part) => part.trim());
  const key = parts.pop() ?? '';
  const modifiers = new Set(parts.map((part) => part.toLowerCase()));
  const ordered = ['mod', 'ctrl', 'alt', 'shift'].filter((name) => modifiers.has(name));
  return [...ordered, canonicalKey(key)].join('+');
}

export function eventBinding(event: KeyboardEvent): string {
  const modifiers: string[] = [];
  // `Mod` is ⌘ on Apple platforms and Ctrl everywhere else, which is what users expect.
  if (event.metaKey || event.ctrlKey) modifiers.push('mod');
  if (event.altKey) modifiers.push('alt');
  if (event.shiftKey) modifiers.push('shift');
  return [...modifiers, canonicalKey(event.key)].join('+');
}

/** A shortcut never fires while focus is inside an input, textarea or contenteditable (K1). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

type Handler = (event: KeyboardEvent) => void;

const handlers = new Map<string, Set<Handler>>();
let attached = false;

function onKeyDown(event: KeyboardEvent) {
  const binding = eventBinding(event);
  for (const shortcut of SHORTCUTS) {
    const registered = handlers.get(shortcut.id);
    if (!registered || registered.size === 0) continue;
    const bindings = shortcut.keys.map(normaliseBinding);
    /**
     * A shifted punctuation key reaches us in three shapes depending on the layout and on who
     * synthesised the event: `?`, `shift+?` and `shift+/` are all the same keystroke. Accept
     * every one of them rather than making the binding table layout-specific.
     */
    const alternates = new Set([binding]);
    if (binding === 'shift+/') alternates.add('?');
    if (binding.startsWith('shift+') && binding.length === 'shift+'.length + 1) {
      alternates.add(binding.slice('shift+'.length));
    }
    if (![...alternates].some((candidate) => bindings.includes(candidate))) continue;
    if (!shortcut.allowInInput && isTypingTarget(event.target)) continue;
    event.preventDefault();
    for (const handler of registered) handler(event);
    return;
    /* Only the first *registered* id for a binding fires: `Esc` appears three times in
       monday's sheet (item page / dialogs / search) but is one keystroke, and the shell's
       single `escape` handler already closes exactly one layer at a time (P5). */
  }
}

/**
 * Registers `handler` against the registry entry `id`. There are no raw `keydown` listeners
 * anywhere else in the app — that is what makes the sheet exhaustive.
 */
export function useShortcut(id: ShortcutId | (string & {}), handler: Handler, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    if (!BY_ID.has(id)) {
      console.error(`useShortcut("${id}") is not in the registry; add it to SHORTCUTS first.`);
      return;
    }

    const set = handlers.get(id) ?? new Set<Handler>();
    set.add(handler);
    handlers.set(id, set);

    if (!attached && typeof window !== 'undefined') {
      window.addEventListener('keydown', onKeyDown);
      attached = true;
    }

    return () => {
      set.delete(handler);
      if (set.size === 0) handlers.delete(id);
      if (handlers.size === 0 && attached && typeof window !== 'undefined') {
        window.removeEventListener('keydown', onKeyDown);
        attached = false;
      }
    };
  }, [id, handler, enabled]);
}

/** Rendered form of a binding, per platform. */
export function renderBinding(binding: string, isApple = detectApple()): string {
  return binding
    .replace('Mod', isApple ? '⌘' : 'Ctrl')
    .replace('Shift', isApple ? '⇧' : 'Shift')
    .split('+')
    .join(isApple ? '' : '+');
}

function detectApple(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);
}
